"use client";

import type { ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export interface LoginLayoutProps {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * 卡片上方的品牌標記（logo 方塊內的文字，如「心」）。有給才渲染 lockup 區塊，
   * 沒給則維持純卡片版型 —— 前台 web 的登入頁沿用同一版型但不掛後台品牌。
   */
  brandMark?: ReactNode;
  /** 品牌名（如「心成後台」），與 brandMark 搭配顯示。 */
  brandName?: ReactNode;
  /** 品牌名下方的小字說明（如「心成教育管理系統」）。 */
  brandTagline?: ReactNode;
}

/**
 * 登入頁版型：暖黑漸層底 + 置中白卡，供 web/admin 各自帶入表單。
 *
 * 2026-09-02 視覺翻新：加上品牌 lockup（金色 logo 方塊 + 系統名），卡片放大到
 * 380px、圓角 16px、升為 shadow-lg。原本純灰底 + 無品牌，登入頁看不出是哪個系統。
 */
export function LoginLayout({
  title,
  subtitle,
  children,
  footer,
  brandMark,
  brandName,
  brandTagline,
}: LoginLayoutProps) {
  useInjectStyles();
  const hasLockup = brandMark !== undefined || brandName !== undefined;
  return (
    <div className="cui-reset cui-login-wrap">
      <div className="cui-login-inner">
        {hasLockup && (
          <div className="cui-login-lockup">
            {brandMark !== undefined && <div className="cui-login-mark">{brandMark}</div>}
            {brandName !== undefined && <h1 className="cui-login-brand">{brandName}</h1>}
            {brandTagline !== undefined && <p className="cui-login-tagline">{brandTagline}</p>}
          </div>
        )}
        <div className="cui-login-card">
          {/* lockup 已經承載主標題時，卡片內標題降為次級樣式，避免兩個 h1 打架 */}
          {hasLockup ? (
            <h2 className="cui-login-title">{title}</h2>
          ) : (
            <h1 className="cui-login-brand" style={{ color: "var(--cui-text)", marginBottom: 4 }}>
              {title}
            </h1>
          )}
          {subtitle && <p className="cui-login-subtitle">{subtitle}</p>}
          {children}
        </div>
        {footer && <div className="cui-login-footer">{footer}</div>}
      </div>
    </div>
  );
}
